import type { AgencyOrgInvoiceDisplayConfigResponse } from "../agency-logic/agency-api-calls/AgencyInvoiceDisplayConfigInterfaces";
import { OrgDetailsAgencyDisplayStatus } from "./OrgDetailsAgencyDisplayStatus";
import { OrgLocationDisplayStatus } from "./OrgLocationDisplayStatus";
import { UserType } from "../remoteExecution/RemoteExecutionServiceImpl";

interface EmailAddress {
  value: string;
}

interface PhoneNumber {
	value: string;
}

interface UserDetails {
	userId?: string | null;
	primaryWorkyUserId?: string | null;
	createdDateTime?: string | null;
	userEmail: EmailAddress;
	userPhoneNumber: PhoneNumber;
	lastName: string;
	firstName: string;
}

enum OrgJobberMatchingType {
  /**
   * This means that for a jobber to qualify for this org location,
   * they need to be present in the pre vetted list
   * 
   * careers.scoutsecond.server.common.dto.OrgLocation#jobbersMappedToLocation
   */
  REQUIRES_TRAINED_WORKERS_ONLY = "REQUIRES_TRAINED_WORKERS_ONLY",
   /**
     * This status means that no training is needed and any worker in the worker pool can pick this up
     */
    ANY_WORKER ="ANY_WORKER"
}
enum ServicedCity {
	MISSISSAUGA = 'MISSISSAUGA',
	TORONTO = 'TORONTO',
	BRAMPTON = 'BRAMPTON',
	OAKVILLE = 'OAKVILLE',
	VAUGHAN = 'VAUGHAN',
	MARKHAM = 'MARKHAM',
	MILTON = 'MILTON',
}

interface WorkyObjectMetaData {
  updatedDateTimeEpoch?: number | null;
  updatedDateTime?: string | null;
  version?: number | null;
  submissionDateTimeEpochSeconds?: number | null;
  submissionDateTime?: string | null;
  testData?: boolean | null;
}

interface WorkyTime {
  hour: number;
  minute: number;
  isAM: boolean;
}

/**
 * Enum representing various jobber statuses.
 */
enum JobberStatus {
  /**
   * For any user in Worky, if they belong to an Org/Client or is a Worky
   * Admin this is their jobber status
   */
  NOT_JOBBER = "NOT_JOBBER",
  /**
   * This indicates that the user has been added automatically to the system
   * by their employer(typically external org), now ew need to complete
   * specific onboarding requirements
   */
  REGISTERED_BY_ORG_AGENCY_NEEDS_TO_ON_BOARD = "REGISTERED_BY_ORG_AGENCY_NEEDS_TO_ON_BOARD",

  /**
   * Jobber has successfully submitted their details to AWS Cognito
   */
  REGISTERED_ON_COGNITO_COMPLETED = "REGISTERED_ON_COGNITO_COMPLETED",

  /**
   * Jobber has successfully submitted their details to AWS Cognito.
   * AND the jobber has submitted the first Jobber On-boarding form
   */
  REGISTERED_SUBMITTED_JOBBER_REG_FORM = "REGISTERED_SUBMITTED_JOBBER_REG_FORM",

  /**
   * THE Jobber at this point has fully registered all
   * their details on worky,
   * registered successfully: email, phone, SR Options
   *
   * Now they just need to submit the docs(tax stuff, id stuff)
   */
  REGISTERED_FULLY_VERIFIED_STILL_NEEDS_TO_SUBMIT_FORMS = "REGISTERED_FULLY_VERIFIED_STILL_NEEDS_TO_SUBMIT_FORMS",

  REGISTERED_NEEDS_TO_COMPLETE_ONBOARDING_TEMPLATE = "REGISTERED_NEEDS_TO_COMPLETE_ONBOARDING_TEMPLATE",

  /**
   * Jobber has submitted the necessary accounting/regulation info to be considered for SRs
   */
  FULLY_HIRED = "FULLY_HIRED",

  CANCELLED = "CANCELLED",

  DROPPED = "DROPPED",
}

interface CancelShiftRequestObj {
  shiftId: string;
}

interface ShiftStartEndDetails {
  startDateTimeEpochSec: number;
  endDateTimeEpochSec: number;
}

/**
 * Represents a per-day time override for a specific shift date.
 * dateKey is an ISO date string (YYYY-MM-DD) used as a map key (shift start calendar day).
 * endDayOffsetDays: calendar days to add to dateKey for the end wall-clock
 * (0 = same day, 1 = overnight into the next calendar day).
 */
interface PerDayTimeOverride {
  readonly dateKey: string;
  readonly startTime: WorkyTime;
  readonly endTime: WorkyTime;
  readonly endDayOffsetDays?: number;
}

enum CorpType {
  AGENCY = "AGENCY",
  ORG = "ORG",
  WORKY = "WORKY",
}
interface CorpJobberDetails {
  corpType: CorpType;
  corpName: string;
  corpNameDisplayName: string;
  enabledFeatures: string[];
  primaryWorkyCorpId: string;
}

enum CorpStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

enum PreferredCommunicationMethod {
  EMAIL = "EMAIL",
  PHONE = "PHONE",
  SMS = "SMS",
  ALL = "ALL",
}


enum JobberResponseStatus {
  NOTIFIED = "NOTIFIED",
  ACCEPTED_AND_CORP_REQUIRES_MANUAL_REVIEW = "ACCEPTED_AND_CORP_REQUIRES_MANUAL_REVIEW",
  ACCEPTED_AND_ASSIGNED = "ACCEPTED_AND_ASSIGNED",
  ACCEPTED_BUT_SHIFT_WAS_ALREADY_ASSIGNED = "ACCEPTED_BUT_SHIFT_WAS_ALREADY_ASSIGNED",
  ACCEPTED_BUT_JOBBER_ALREADY_BOOKED = "ACCEPTED_BUT_JOBBER_ALREADY_BOOKED",
  ACCEPTED_BUT_JOBBER_EXCEEDS_MAX_HOURS = "ACCEPTED_BUT_JOBBER_EXCEEDS_MAX_HOURS",
  DENIED_CORP = "DENIED_CORP",
  DENIED_JOBBER = "DENIED_JOBBER",
}

interface CommunicationPreferences {
  defaultEmail: EmailAddress;
  isEmailVerified: boolean;
  defaultPhoneNumber: PhoneNumber;
  preferredCommunicationMethod: PreferredCommunicationMethod;
}

interface AddressDto {
	street: string;
	unitNumber: string;
	city: string;
	province: string;
	postalCode: string;
	country?: string;
	majorIntersection?: string;
	entranceInstructions?: string;
}



interface OrgLocation {
  orgName: string;
  locationName: string;
  /** Resolved UI label from the server; when missing, clients should fall back to `locationName`. */
  locationDisplayName: string;
  /**
   * Visibility for new work. DISABLED hides the site from pickers. Omitted or null is ACTIVE.
   */
  orgLocationDisplayStatus?: OrgLocationDisplayStatus | OrgLocationDisplayStatus.ACTIVE;
  locationAddress?: AddressDto;
  servicedCity?: ServicedCity;
  /**
   * Work jurisdiction for this facility (wire value matching WorkJurisdictionIdentifier).
   * Used to derive the default shift wall-clock timezone (e.g. BRITISH_COLUMBIA -> America/Vancouver;
   * ALBERTA -> America/Edmonton).
   */
  workJurisdictionIdentifier?: string;
  primaryShiftManagerContact?: UserDetails;
  primaryHRManagerContact?: UserDetails;
  orgUserContactsForLocation?: UserDetails[];
  orgJobberMatchingType?: OrgJobberMatchingType;
}
enum BillingTimeInterval {
  WEEKLY = "WEEKLY",
  /**
   * Will default to 15th of the month, and end of the month
   */
  BI_MONTHLY = "BI_MONTHLY",
  // every 2 weeks, can split month. Basically add 14 days to the start date
  BI_WEEKLY = "BI_WEEKLY",
  /**
   * Will default to end of the month
   */
  MONTHLY = "MONTHLY",
  QUARTERLY = "QUARTERLY"
}

/**
 * Per-jobber override rates for an org worker type. Includes orgBillingRate for display.
 */
interface JobberOverrideRatesDTO {
  readonly jobberDetails: WorkyUserDetailsZip;
  /** Optional: jobber pay rate for payroll. */
  readonly jobberPayRate?: number | null;
  /** Org billing rate for this jobber override (computed or explicit). Displayed directly on the DTO. */
  readonly orgBillingRate?: number | null;
}

interface AgencyOrgJobberWorkerTypeDetailsDTO {
  jobberWorkerTypeDTO: JobberWorkerTypeDTO;
  /**
   * Billing calculation base for this org worker type (not payroll pay rate).
   */
  hourlyBaseRate: number;
  /**
   * Org-wide default hourly pay for all workers with this role at this client (payroll).
   */
  readonly orgDefaultJobberPayRate?: number | null;
  /**
   * The hourly-rate the org will pay for the jobber worker type
   */
  defaultOrgBillingPayRate?: number;
  /**
   * Per-jobber override rates for this org worker type. Each override includes orgBillingRate for display.
   */
  jobberOverrideRates?: readonly JobberOverrideRatesDTO[] | null;
  /** Layer B sparse labor sales-tax rules for this client × worker type (ON/BC/AB). */
  readonly laborSalesTaxRules?: readonly LaborSalesTaxRuleDTO[] | null;
}

interface AgencyToOrgDetailsForInvoiceCreationDTO {
  readonly orgEmailAddressForInvoice?: string | null;
  readonly orgNameForInvoiceOverride?: string | null;
  readonly orgManagerForInvoiceFirstName?: string | null;
  readonly orgManagerForInvoiceLastName?: string | null;
}

interface AgencyOrgInvoiceConfigSpecDTO {
  readonly agencyToOrgDetailsForInvoiceCreation?: AgencyToOrgDetailsForInvoiceCreationDTO | null;
  readonly noteForInvoicingDisplay?: string | null;
  readonly configLastModificationDateTime: number;
  readonly invoiceDisplayConfig?: AgencyOrgInvoiceDisplayConfigResponse | null;
}

interface AgencyOrgClientDetailsResponseDTO {
  readonly orgName: string;
  readonly displayName?: string | null;
  readonly invoiceConfigSpec?: AgencyOrgInvoiceConfigSpecDTO | null;
  readonly parallelInvoicingConfigs: readonly ParallelAgencyOrgBillingCycleSummaryConfigClientDTO[];
}

interface UpdateAgencyOrgInvoiceConfigSpecRequestDTO {
  readonly orgName: string;
  readonly noteForInvoicingDisplay?: string | null;
}

/** Client DTO for a single premium pay rule reference in parallel org billing config. */
interface ParallelAgencyOrgWorkerPayPremiumPayRuleClientDTO {
  readonly ruleId: string;
}

/** Client DTO for worker pay rate config in parallel org billing. */
interface ParallelAgencyOrgWorkerPayRateConfigClientDTO {
  readonly baseBillingRateAmount: number;
  readonly payRateType: string;
  readonly parallelAgencyOrgWorkerPayPremiumPayRules?: readonly ParallelAgencyOrgWorkerPayPremiumPayRuleClientDTO[] | null;
}

/** Client DTO for tax applicability per jurisdiction in parallel org billing. */
interface TaxApplicabilityConfigClientDTO {
  readonly recordId: string;
  readonly status: string;
  readonly recordCreatedDateTimeEpochSec: number;
  readonly jurisdictionIdentifier: string;
  readonly taxApplicabilityType: string;
}

/** Client DTO for tax applicability config (per jurisdiction list) in parallel org billing. */
interface ParallelAgencyOrgBillTaxApplicabilityConfigClientDTO {
  readonly applicabilityConfigPerJurisdiction?: readonly TaxApplicabilityConfigClientDTO[] | null;
}

/** Client DTO for one worker type's config in a parallel org billing cycle summary. */
interface JobberWorkerTypeParallelAgencyOrgBillingConfigClientDTO {
  readonly recordId: string;
  readonly status: string;
  readonly recordCreatedDateTimeEpochSec: number;
  readonly workerTypeUniqueIdentifier: string;
  readonly parallelAgencyOrgBillTaxApplicabilityConfig?: ParallelAgencyOrgBillTaxApplicabilityConfigClientDTO | null;
  readonly parallelAgencyOrgWorkerPayRateConfig: ParallelAgencyOrgWorkerPayRateConfigClientDTO;
}

/** Client DTO for a parallel agency org billing cycle summary config (parallel invoicing config). */
interface ParallelAgencyOrgBillingCycleSummaryConfigClientDTO {
  readonly recordId: string;
  readonly invoiceConfigTypeName: string;
  readonly status: string;
  readonly recordCreatedDateTimeEpochSec: number;
  readonly invoicingType: string;
  readonly parallelInvoicingWorkerTypesConfigs: readonly JobberWorkerTypeParallelAgencyOrgBillingConfigClientDTO[];
  readonly invoiceDisplayConfig?: AgencyOrgInvoiceDisplayConfigResponse | null;
}

interface OrgLocationMapDetails {
  orgLocationMap: Map<string, OrgLocation>;
  /** Facilities hidden from pickers (DISABLED). Present so client details can re-enable them. */
  disabledOrgLocationMap?: Map<string, OrgLocation> | Record<string, OrgLocation>;
  agencyOrgJobberWorkerTypeDetails?: AgencyOrgJobberWorkerTypeDetailsDTO[];
  billingTimeInterval: BillingTimeInterval;
  billingDayOfTheWeek: string;
  /** Start-of-day Eastern epoch for the first weekly/bi-weekly billing cycle. */
  billingCycleAnchorDateTimeEpochSec?: number;
  /** Tax authority for this org's client billing (wire value matching WorkJurisdictionIdentifier). */
  workJurisdictionIdentifier?: string;
  /** Override display name for this org; null/empty means use org name. */
  displayName: string;
  /** When DISABLED, client is omitted from agency UI lists/pickers; omitted or null is treated as ACTIVE. */
  orgDetailsAgencyDisplayStatus: OrgDetailsAgencyDisplayStatus | OrgDetailsAgencyDisplayStatus.ACTIVE;
  invoiceConfigSpec?: AgencyOrgInvoiceConfigSpecDTO;
  /** Parallel invoicing configs for this org/agency (client representation of OrgDetailsAgency#getParallelInvoicingConfigs()). */
  parallelInvoicingConfigs?: readonly ParallelAgencyOrgBillingCycleSummaryConfigClientDTO[] | null;
  /**
   * Org-level work-task template config (auto-apply + prefill) for this client.
   * Null/omitted means none configured.
   */
  orgWorkTaskTemplateConfig?: OrgWorkTaskTemplateConfigClientDTO | null;
}

/** One auto-apply work-task template entry on an org (wire detail from server). */
interface OrgAutoApplyWorkTaskTemplateDetailClientDTO {
  readonly workTaskTemplateId: string;
}

enum WorkTaskActivityTypeDisplayMode {
  SHOW = 'SHOW',
  DISABLE = 'DISABLE',
}

enum WorkTaskQuestionConfigStatus {
  DISABLED = 'DISABLED',
}

/** Persisted display mode for one activity row in a client-specific activity grid. */
interface WorkTaskActivityTypeDisplayConfigClientDTO {
  readonly activityTypeId: string;
  readonly displayMode: WorkTaskActivityTypeDisplayMode | string;
}

/** Persisted client-specific row display configuration for one activity grid question. */
interface WorkTaskActivityGridDisplayConfigClientDTO {
  readonly questionId: string;
  readonly activityTypes: readonly WorkTaskActivityTypeDisplayConfigClientDTO[];
}

/** Persisted activity grid display configuration for one work-task template. */
interface WorkTaskActivityGridDisplayTemplateDetailClientDTO {
  readonly workTaskTemplateId: string;
  readonly activityGridDisplayConfigs: readonly WorkTaskActivityGridDisplayConfigClientDTO[];
}

/** One org-level static prefill answer for a work-task template question. */
interface WorkTaskTemplatePreFillAnswerDetailClientDTO {
  readonly questionId: string;
  readonly textAnswer: string;
}

/** Prefill answers for one work-task template on an org. */
interface WorkTaskTemplatePreFillTemplateDetailClientDTO {
  readonly workTaskTemplateId: string;
  readonly preFillAnswers: readonly WorkTaskTemplatePreFillAnswerDetailClientDTO[];
}

/** Task question settings for one work-task template on an org. */
interface WorkTaskQuestionConfigDetailClientDTO {
  readonly questionId: string;
  readonly questionStatus: WorkTaskQuestionConfigStatus | string;
}

interface WorkTaskQuestionConfigTemplateDetailClientDTO {
  readonly workTaskTemplateId: string;
  readonly questionConfigs: readonly WorkTaskQuestionConfigDetailClientDTO[];
}

/** Org-level work-task template answer prefill config. */
interface WorkTaskTemplatePreFillConfigClientDTO {
  readonly templatePreFillDetails: readonly WorkTaskTemplatePreFillTemplateDetailClientDTO[];
}

/** Org-level work-task template config (auto-apply + prefill) returned on agency org details. */
interface OrgWorkTaskTemplateConfigClientDTO {
  readonly autoApplyWorkTaskTemplateDetails: readonly OrgAutoApplyWorkTaskTemplateDetailClientDTO[];
  readonly workTaskTemplatePreFillConfig?: WorkTaskTemplatePreFillConfigClientDTO | null;
  readonly activityGridDisplayTemplateDetails?:
    readonly WorkTaskActivityGridDisplayTemplateDetailClientDTO[] | null;
  readonly questionConfigTemplateDetails?:
    readonly WorkTaskQuestionConfigTemplateDetailClientDTO[] | null;
  readonly workTaskDisplayConfigTemplateId?: string | null;
}

enum WorkyPayDisplayConfig {
  WORKY_PAY_WORKER_VIEW_OFF = 'WORKY_PAY_WORKER_VIEW_OFF',
}

interface WorkyPayFeatureDetailsWorkerView {
  readonly workyPayDisplayConfig?: WorkyPayDisplayConfig | null;
}

interface WorkyWorkerFeaturesViewConfig {
  readonly workyPayFeatureDetailsWorkerView?: WorkyPayFeatureDetailsWorkerView | null;
}

interface WorkyFeatureDetails {
  readonly workyWorkerFeaturesViewConfig?: WorkyWorkerFeaturesViewConfig | null;
}

interface WorkyUserDetails {
  userDetails: UserDetails;
  userType: UserType;
  jobberStatus?: JobberStatus;
  profilePicUrl:string;
  corpJobberDetails: CorpJobberDetails;
  primaryWorkyUserId?: string | null;
  workyFeatureDetails?: WorkyFeatureDetails | null;
}
interface WorkyUserDetailsZip{
  userDetails: UserDetails;
  userType: UserType;
  jobberStatus?: JobberStatus | null;
  profilePicUrl?: string | null;
  primaryWorkyUserId?: string | null;
}

interface WorkyProductUserIdsDTO {
  readonly primaryWorkyUserId: string;
}

interface UserDetailsMetaDataDTO {
  readonly createdDateTimeEpochSec: number;
}

interface WorkyUserDetailsV2DTO {
  readonly userDetails: UserDetails;
  readonly userType: UserType;
  readonly corpJobberDetails: CorpJobberDetails;
  readonly productUserIds: WorkyProductUserIdsDTO;
  readonly profilePicUrl?: string | null;
  readonly userDetailsMetaData?: UserDetailsMetaDataDTO | null;
}

/**
 * Lightweight org portal / end-client user stamped on SR/SA/SP for display.
 * Org name is available via workyUserDetailsV2.corpJobberDetails.corpName.
 */
interface EndClientDetailsSimple {
  readonly orgDisplayName: string;
  readonly workyUserDetailsV2: WorkyUserDetailsV2DTO;
}

interface SRJobberResponseObjDTO {
  jobberResponseId: string;
  jobberDetails: WorkyUserDetails;
  jobberResponseTime: number;
  jobberResponseStatus?: JobberResponseStatus | null;
  jobberResponseNote?: string | null;
}

interface SRJobberResponseObjDTO {
  jobberResponseId: string;
  jobberDetails: WorkyUserDetails;
  jobberResponseTime: number;
  jobberResponseStatus?: JobberResponseStatus | null;
  jobberResponseNote?: string | null;
}

interface OrgLocationSimpleClient {
  simpleLocation: string;
  locationName: string;
  locationDisplayName: string;
  locationAddress?: any;
  /**
   * Work jurisdiction for this facility (wire value matching WorkJurisdictionIdentifier).
   * Used to derive the default shift wall-clock timezone for display.
   */
  workJurisdictionIdentifier?: string;
}
enum ShiftRequestStatus{
  CREATED='CREATED',
  ASSIGNED='ASSIGNED',
  REQUIRES_REVIEW='REQUIRES_REVIEW'
}
interface ShiftRequestUnit {
	shiftRequestUnitId: string;
	orgLocationSimpleClient?: OrgLocationSimpleClient;
	parentShiftRequestId: string;
	shiftRequestStatus: ShiftRequestStatus;
	workerType: string;
  jobberWorkerTypes?: JobberWorkerTypeDTO[];
  requestingUserDetails: WorkyUserDetails;
  /** Optional org portal / end-client user this shift is for. */
  endClientDetails?: EndClientDetailsSimple | null;
  jobberResponses?: readonly SRJobberResponseObjDTO[];
  assignedAgencyName?: string;
	workyWorkdayShift: string;
	simpleLocation: string;
	orgName: string;
	jobberHourlyRate: number;
	orgHourlyRate: number;
	startDateTimeEpochSeconds: number;
	endDateTimeEpochSeconds: number;
	startDateTime: string;
	endDateTime: string;
	startDay: string;
  jobberEmailsAlreadyNotified?: string[];
	startTime: number;
	submissionDateTimeEpochSeconds: number;
	submissionDateTime: string;		
	orgAddress: {
		street: string;
		unitNumber: string;
		city: string;
		province: string;
		postalCode: string;
		country: string;
		majorIntersection: string;
		entranceInstructions: string;
	};
	lastJobberPoolNotifTime: null | string;
	lastJobberPoolNotifTimeEpochSec: null | number;
	preferredJobbers: {
		value: string;
	}[];
	updatedDateTimeEpoch: number;	
	updatedDateTime: string;		
	assignedJobberProfilePicS3URL:string;
	assignedJobberUserDetails?:UserDetails;
  assignedJobberUserDetailsAfterSACreate?: UserDetails | null;
}


interface Holiday {
  holidayTitle: string;
  holidayDate: string;  // e.g., "September 2"
}

interface PayrollDisplay {
  payrollType: "Weekly" | "Bi-Weekly" | "Monthly" | "One-Time";
  dateRange: string;  // e.g., "Sep 2 - Sep 9", "Sep 13"
  status: "Processed" | "Pending" | "Error" | "Completed" | "Overdue";
}

interface ModifyShiftTimeRequest {
  shiftId: string;
  newShiftStartTime?: number;
  newShiftEndTime?: number;
  reason?: string;
  }

/** Request to change a shift's facility location in place. */
interface ModifyShiftLocationRequest {
  readonly shiftId: string;
  readonly newOrgLocationName: string;
  readonly reason?: string;
}

/** Request to change a shift's worker type in place. */
interface ModifyShiftWorkerTypeRequest {
  readonly shiftId: string;
  readonly newWorkerTypeUniqueIdentifier: string;
  readonly reason?: string;
}

/** Response from shift/modify/shiftTime, shift/modify/location, and shift/modify/workerType APIs */
interface ShiftTimeModificationResp {
  readonly statusCode: string;
  readonly message: string;
}
interface LocalStorageObjects{
  // keyName, value
  attributes: Record<string, string>
}

interface CorpDataObjSimpleFromLocalStorage{
    corpJobberDetails?: CorpJobberDetails
    corpIconPublicURL?: string
}


enum OrgHourlyRateMarkupType {
  /**
   * This means the org will be charged a percentage markup on top of the defaultJobberPayRate.
   */
  PERCENTAGE_RATE_MARKUP_TYPE ="PERCENTAGE_RATE_MARKUP_TYPE",

  /**
   * This means that when the org and agency signed up,
   * they established a default hourly rate for the org
   * for this jobberworkerType regardless of how much the
   * actual jobber gets paid.
   */
  DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE="DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE",

  /**
   * This means the org and agency agreed on a fixed dollar
   * amount to be added on top of the defaultJobberPayRate.
   * E.g.
   */
  HOURLY_RATE_MARKUP_TYPE="HOURLY_RATE_MARKUP_TYPE"
}

enum JobberWorkerTypeStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

type LaborSalesTaxApplicability =
  | 'TAX_APPLICABLE_ON_FULL_AMOUNT'
  | 'NO_TAX_AT_ALL';

/** Labor sales-tax config jurisdictions (ON/BC/AB only; CANADA is not stored). */
type LaborSalesTaxWorkJurisdictionIdentifier =
  | 'ONTARIO'
  | 'BRITISH_COLUMBIA'
  | 'ALBERTA';

interface LaborSalesTaxRuleDTO {
  readonly workJurisdictionIdentifier: LaborSalesTaxWorkJurisdictionIdentifier;
  readonly laborSalesTaxApplicability: LaborSalesTaxApplicability;
  /** Fraction in [0, 1]; null/undefined = use platform rate when applicable. */
  readonly taxRate?: number | null;
}

interface JobberWorkerTypeAgencyPrivateViewDTO{
  workerTypeName: string;
  workerTypeUniqueIdentifier: string;
  defaultJobberPayRate: number;
  orgHourlyRateMarkupType: OrgHourlyRateMarkupType;
  defaultOrgBillingPayRate: number;
  orgDefaultJobberPayRateForPayroll?: number | null;
  /**
   * Optional; when set, used for invoice generation for the org instead of workerTypeName.
   */
  orgBillingWorkerTypeName?: string;
  readonly status?: JobberWorkerTypeStatus;
  /** Layer A sparse labor sales-tax rules (ON/BC/AB). */
  readonly laborSalesTaxRules?: readonly LaborSalesTaxRuleDTO[] | null;
}

interface JobberWorkerTypeDTO {
  workerTypeName: string;
  /**
   * In the future if client wants to change workerTypeName
   * it can be modified w/o impacting the backend search criteria
   * as we will be using uuid
   */
  workerTypeUniqueIdentifier: string;
  /**
     * Optional; when set, used for invoice generation for the org instead of workerTypeName.
     */
  orgBillingWorkerTypeName?: string;
  readonly status?: JobberWorkerTypeStatus;
}

interface LeadValueFeedbackObj{
  /**
   * This is the number of the feedback given
   * -100 for wrong, 0 for initial status, 100 for correct
   */
  leadElementFeedbackNumber: string;
  /**
   * This is the name of the feedback name given
   */
  leadElementFeedbackName: string;
  feedbackGivenOnDateTimeEpocSec: number;
}

interface ExternalVendorKeyValue{
    /**
     * The name of the vendor we sourced the data from
     */
    vendorSourceName?: string;
    /**
     * The id of the value as per the vendor we sourced the data from
     */
    vendorSourceId?: string;
    /**
     * The value of the field from the vendor
     */
    value?: string;
    feedbackObj?: LeadValueFeedbackObj;
}

interface WorkyCustomField{
  presentationValue: string;
  backendLookUp: string;
}

export {
  ExternalVendorKeyValue,
  ModifyShiftTimeRequest,
  ModifyShiftLocationRequest,
  ModifyShiftWorkerTypeRequest,
  ShiftTimeModificationResp,
  WorkyObjectMetaData,
  JobberStatus,
  WorkyTime,
  CancelShiftRequestObj,
  ShiftStartEndDetails,
  OrgLocationMapDetails, 
  OrgLocation,
  OrgLocationSimpleClient,
  EmailAddress,
  PhoneNumber,
  CorpType,
  CorpJobberDetails,
  CorpStatus,
  CommunicationPreferences,
  ServicedCity,
  UserDetails,
  AddressDto,
  ShiftRequestUnit,
  WorkyPayDisplayConfig,
  WorkyPayFeatureDetailsWorkerView,
  WorkyWorkerFeaturesViewConfig,
  WorkyFeatureDetails,
  WorkyUserDetails,
  Holiday,
  PayrollDisplay,
  UserType,
  LocalStorageObjects,    
  CorpDataObjSimpleFromLocalStorage,
  PreferredCommunicationMethod,
  JobberWorkerTypeStatus,
  JobberWorkerTypeDTO,
  OrgHourlyRateMarkupType,
  JobberWorkerTypeAgencyPrivateViewDTO,
  LaborSalesTaxApplicability,
  LaborSalesTaxWorkJurisdictionIdentifier,
  LaborSalesTaxRuleDTO,
  JobberOverrideRatesDTO,
  AgencyOrgJobberWorkerTypeDetailsDTO,
  WorkyCustomField,
  WorkyUserDetailsZip,
  UserDetailsMetaDataDTO,
  WorkyProductUserIdsDTO,
  WorkyUserDetailsV2DTO,
  EndClientDetailsSimple,
  OrgJobberMatchingType,
  PerDayTimeOverride,
  JobberResponseStatus,
  SRJobberResponseObjDTO,
  ShiftRequestStatus,
  BillingTimeInterval,
  AgencyToOrgDetailsForInvoiceCreationDTO,
  AgencyOrgInvoiceConfigSpecDTO,
  AgencyOrgClientDetailsResponseDTO,
  UpdateAgencyOrgInvoiceConfigSpecRequestDTO,
  ParallelAgencyOrgWorkerPayPremiumPayRuleClientDTO,
  ParallelAgencyOrgWorkerPayRateConfigClientDTO,
  TaxApplicabilityConfigClientDTO,
  ParallelAgencyOrgBillTaxApplicabilityConfigClientDTO,
  JobberWorkerTypeParallelAgencyOrgBillingConfigClientDTO,
  ParallelAgencyOrgBillingCycleSummaryConfigClientDTO,
  OrgDetailsAgencyDisplayStatus,
  OrgLocationDisplayStatus,
  OrgAutoApplyWorkTaskTemplateDetailClientDTO,
  WorkTaskActivityTypeDisplayMode,
  WorkTaskQuestionConfigStatus,
  WorkTaskActivityTypeDisplayConfigClientDTO,
  WorkTaskActivityGridDisplayConfigClientDTO,
  WorkTaskActivityGridDisplayTemplateDetailClientDTO,
  WorkTaskTemplatePreFillAnswerDetailClientDTO,
  WorkTaskTemplatePreFillTemplateDetailClientDTO,
  WorkTaskTemplatePreFillConfigClientDTO,
  WorkTaskQuestionConfigDetailClientDTO,
  WorkTaskQuestionConfigTemplateDetailClientDTO,
  OrgWorkTaskTemplateConfigClientDTO,
};
